import React, { useEffect } from 'react';

import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CStatefulContainer from '../../../components/CStatefulContainer';
import Page from '../../../components/layout/Page';
import { EAppType } from '../../../enums/AppType';
import { updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { useUsageTrackingInfo } from '../../../hooks/useUsageTrackingInfo';
import type { CachedAPIResponse } from '../../../models/CachedAPIResponse';
import type { IResponse } from '../../../models/IResponse';
import type { PlotlyResponse } from '../../../models/PlotlyModel';
import type { IUsageTrackingInfo } from '../../../models/usage-tracking/IUsageTracking';
import SvOpsNewsServices from '../../../services/SvOpsNewsServices';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import type { PortfolioManagementOutletContext } from '../PPortfolioManagementOutlet';
import { useOutletContext } from 'react-router-dom';

interface State {
  tradingPlots: IResponse<
    CachedAPIResponse<{
      turnover_comparison: PlotlyResponse;
      buy_sell_comparison: PlotlyResponse;
    }>
  >;
}

export default function PPorfolioManagementMisc() {
  const portfolioManagementMiscHeading = CLocalizedTextStringStatic('portfolioManagement.nav.misc');
  const portfolioManagementMiscTitle = CLocalizedTextStringStatic('portfolioManagement.tab.misc.title');
  const portfolioManagementMiscDescription = CLocalizedTextStringStatic('portfolioManagement.tab.misc.description');

  const { locale } = useAppSelector((state) => state.dashboard);
  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.PortfolioManagementMiscellaneous);
  const { factsheetId } = useOutletContext<PortfolioManagementOutletContext>();
  const factsheetID = factsheetId ?? '';
  const [state, setState, getState] = useStateExtended<State>({
    tradingPlots: { state: { isLoading: true, isError: false } }
  });

  useEffect(() => {
    (async () => {
      const currentState = await getState();
      await updateStateResponseStatefulAttribute<State, typeof state.tradingPlots.data>(
        { tradingPlots: currentState.tradingPlots },
        setState,
        async () =>
          await SvOpsNewsServices.getTradingPlots({
            factsheetID
          })
      );
    })();
  }, [locale,factsheetID]);

  useEffect(() => {
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, []);

  return (
    <Page title={portfolioManagementMiscHeading}>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <h3>{portfolioManagementMiscTitle}</h3>
          <p>{portfolioManagementMiscDescription}</p>
        </CPageModule>
        <CStatefulContainer
          apiRequestState={state.tradingPlots.state}
          showContent={!!state.tradingPlots.data}
          submodule
        >
          <CPageModule fullWidth style={{ paddingTop: 0 }}>
            <CPageModule submodule>
              <h4>{state.tradingPlots.data?.cacheData.turnover_comparison?.layout?.title?.text}</h4>
            </CPageModule>
            <CPageModulePlot
              data={state.tradingPlots.data?.cacheData.turnover_comparison.data}
              layout={state.tradingPlots.data?.cacheData.turnover_comparison.layout}
            />
          </CPageModule>

          <CPageModule fullWidth style={{ paddingTop: 0 }}>
            <CPageModule submodule>
              <h4>{state.tradingPlots.data?.cacheData.buy_sell_comparison?.layout?.title?.text}</h4>
            </CPageModule>
            <CPageModulePlot
              data={state.tradingPlots.data?.cacheData.buy_sell_comparison.data}
              layout={state.tradingPlots.data?.cacheData.buy_sell_comparison.layout}
            />
          </CPageModule>
        </CStatefulContainer>
      </CPageModule>
    </Page>
  );
}
